import { useState, useCallback } from "react";
import PageMeta from "../components/PageMeta";
import Navbar from "../components/Navbar";
import { Footer } from "../components/Footer";
import SubscribeModal from "../components/SubscribeModal";
import SectionAnchor from "../components/SectionAnchor";
import HeroSection from "../components/sections/HeroSection";
import ValuePropsSection from "../components/sections/ValuePropsSection";
import HowItWorksSection from "../components/sections/HowItWorksSection";
import PricingSection from "../components/sections/PricingSection";
import FaqSection from "../components/sections/FaqSection";
import CtaBanner from "../components/sections/CtaBanner";
import FounderSection from "../components/sections/FounderSection";
import { useSectionScroll } from "../hooks/useSectionScroll";
import { useConversion } from "../hooks/useConversion";
import { entreprisesContent } from "../content/entreprises";
import { getFounderContent } from "../content/founder";
import { SHOW_FOUNDER_SECTION, SHOW_PRICING_SECTION } from "../constants/config";

export default function EntreprisesPage() {
  const content = entreprisesContent;
  const { refs, scrollTo } = useSectionScroll();
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const { ctaLabel, handlePrimaryAction } = useConversion({
    variant: "entreprise",
    openModal,
  });

  const handleCta = useCallback(
    (location) => handlePrimaryAction(location),
    [handlePrimaryAction]
  );

  return (
    <>
      <PageMeta
        title={content.meta.title}
        description={content.meta.description}
        path="/entreprises"
        faqItems={content.faq.items}
      />
      <Navbar
        variant="entreprise"
        alternateLink={content.alternateLink}
        onScrollTo={scrollTo}
        ctaLabel={ctaLabel}
        onCtaClick={() => handleCta("navbar")}
      />
      <SubscribeModal
        open={modalOpen}
        onClose={closeModal}
        userType="recruteur"
      />

      <main className="pt-16">
        <HeroSection
          title={content.hero.title}
          subtitle={content.hero.subtitle}
          tagline={content.hero.tagline}
          cta={ctaLabel}
          onCtaClick={handleCta}
          variant="entreprise"
          onScrollDown={() => scrollTo("pourquoi")}
          image={content.hero.image}
          imageAlt={content.hero.imageAlt}
        />

        <SectionAnchor
          id="pourquoi"
          ref={refs.pourquoi}
          className="py-16 px-6 bg-gray-50"
        >
          <ValuePropsSection
            title={content.why.title}
            stats={content.why.stats}
            paragraphs={content.why.paragraphs}
            itemsTitle={content.why.itemsTitle}
            items={content.why.items}
            variant="entreprise"
          />
        </SectionAnchor>

        <SectionAnchor
          id="comment"
          ref={refs.comment}
          className="py-16 px-6 bg-white"
        >
          <HowItWorksSection
            title={content.howItWorks.title}
            steps={content.howItWorks.steps}
            image={content.howItWorks.image}
            imageAlt={content.howItWorks.imageAlt}
            variant="entreprise"
          />
        </SectionAnchor>

        {SHOW_PRICING_SECTION && (
          <section className="py-16 px-6 bg-gray-50">
            <PricingSection
              title={content.pricing.title}
              economic={content.economic}
              onCtaClick={handleCta}
              ctaLabel={ctaLabel}
            />
          </section>
        )}

        {SHOW_FOUNDER_SECTION && (
          <section
            className={`py-16 px-6 border-t border-gray-100 ${
              SHOW_PRICING_SECTION ? "bg-white" : "bg-gray-50"
            }`}
          >
            <FounderSection {...getFounderContent()} variant="entreprise" />
          </section>
        )}

        <SectionAnchor
          id="faq"
          ref={refs.faq}
          className={`py-16 px-6 ${
            SHOW_FOUNDER_SECTION || SHOW_PRICING_SECTION ? "bg-gray-50" : "bg-white"
          }`}
        >
          <FaqSection title={content.faq.title} items={content.faq.items} />
        </SectionAnchor>

        <SectionAnchor
          id="inscrire"
          ref={refs.inscrire}
          className="py-16 px-4 sm:px-6 bg-white"
        >
          <div className="container mx-auto max-w-3xl w-full">
            <CtaBanner
              title={content.ctaBanner.title}
              subtitle={content.ctaBanner.subtitle}
              variant="entreprise"
              userType="recruteur"
            />
          </div>
        </SectionAnchor>
      </main>

      <Footer />
    </>
  );
}
